import {css, StyleSheet} from 'aphrodite';
import React, {useRef} from 'react';
import {VariableSizeList} from 'react-window';

import * as analytics from 'analytics/Analytics';
import AutoSizer from 'components/AutoSizer';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PolymerCard} from 'molbio/bulk-assembly/polymer-map-modals/PolymerCard';
import {GRAY} from 'ui-library/Colors';

interface PolymerCardInfo {
  condensedContent?: JSX.Element;
  content: JSX.Element;
}

interface PolymerModalContentProps {
  assemblyId: string;
  activeCardSize: number;
  inactiveCardSize: number;
  activeIndex: number;
  cards: Array<PolymerCardInfo>;
  activePolymerMap: JSX.Element;
  setActiveIndex: (index: number) => void;
}

export function PolymerModalContent({
  assemblyId,
  activeCardSize,
  inactiveCardSize,
  activeIndex,
  cards,
  activePolymerMap,
  setActiveIndex,
}: PolymerModalContentProps): JSX.Element {
  const listRef = useRef<VariableSizeList>(null);

  return (
    <div className={css(styles.modalContents)}>
      <div className={css(styles.panel)}>
        <AutoSizer disableWidth={true}>
          {({height}) => (
            <VariableSizeList
              height={height}
              itemCount={cards.length}
              itemSize={(index) => (index === activeIndex ? activeCardSize : inactiveCardSize)}
              ref={listRef}
              width="100%"
            >
              {({index, style}) => (
                <PolymerCard
                  condensedContent={cards[index].condensedContent}
                  content={cards[index].content}
                  key={index}
                  active={activeIndex === index}
                  isOnlyCard={cards.length === 1}
                  onActivate={() => {
                    // TODO: Generalize to constructs
                    analytics.trackEvent(
                      MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_FRAGMENT_MODAL_ACTIVE_FRAGMENT,
                      {
                        assemblyId,
                        actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_FRAGMENT_MODAL,
                      }
                    );
                    setActiveIndex(index);
                    listRef.current!.resetAfterIndex(Math.min(activeIndex, index));
                  }}
                  style={style}
                />
              )}
            </VariableSizeList>
          )}
        </AutoSizer>
      </div>

      <div className={css(styles.map)}>{activePolymerMap}</div>
    </div>
  );
}

const styles = StyleSheet.create({
  modalContents: {
    display: 'flex',
    width: '100%',
    height: '100%',
  },
  panel: {
    width: '280px',
    borderRight: `1px solid ${GRAY}`,
    overflowY: 'auto',
  },
  map: {
    flex: 1,
    overflowX: 'auto',
  },
});
